import { z } from 'zod';
import { AnnouncementCategory, ComplaintCategory } from '../enums';

/**
 * Every request and query schema, declared once so a DTO change breaks the API
 * and the web build in the same commit.
 *
 * Two conventions, both from `uml/18-api-specification.md` section 2:
 *
 * - **Identifiers are integers.** Every primary key in this system is a
 *   `SERIAL` (gap G1, resolved in `uml/16-architecture-and-repo-audit.md`
 *   section 7.2). `z.string().uuid()` on an id is a leftover from the schema
 *   that was deleted; it validates a value the database cannot hold.
 * - **Paging is `page` and `pageSize`**, section 2.6, `pageSize` capped at 100.
 */

// ============================================================
// PAGING - uml/18 section 2.6
// ============================================================
//
// `PaginationSchema` and `IdParamSchema` moved to ./pagination so the
// per-package schema files can extend them without importing this file, which
// re-exports them. A `limit`-based paging variant was proposed and dropped:
// the response is `{ items, page, pageSize, total }`, and a query saying
// `limit` while the body says `pageSize` is the mismatch nobody notices until
// a second client appears. `Paged<T>` in `../types` is the twin.

import { PaginationSchema } from './pagination';
import { moroccanPhone } from './phone';

export * from './pagination';
export * from './phone';

// ============================================================
// RESTAURANT - uml/18 section 8.1
// ============================================================

export * from './restaurant';

// ============================================================
// ACCESS
// ============================================================

const iscaeEmail = z
  .string()
  .email()
  .endsWith('@groupeiscae.ma', { message: 'Must be an ISCAE email address' });

/** SEC-3. argon2's cost grows with input; nothing legitimate is longer. */
const PASSWORD_MAX = 128;

const strongPassword = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(PASSWORD_MAX, 'Password must be at most 128 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one digit');

export const SignUpSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: iscaeEmail,
  password: strongPassword,
  // RG180, oubay c2f3b5a: asked at sign-up, with his two French messages.
  // Gender is MALE or FEMALE here - UNSPECIFIED stays possible only for an
  // account created before this rule - and decides who may book a ladies-only
  // ride (RG164).
  // One message for every way of not choosing: missing, empty (the form's
  // placeholder option sends ''), or a value outside the two.
  gender: z.enum(['MALE', 'FEMALE'], {
    errorMap: () => ({ message: 'Le genre est obligatoire' }),
  }),
  phone: moroccanPhone,
  // The legal gate. A sign-up that cannot record the student's consent to the
  // privacy policy must not produce an account, and the gate is enforced here
  // by the API - a checkbox in the form is a convenience, a schema field is the
  // referee. `z.literal(true)` also turns "false" and every truthy string a
  // client could invent into a validation error.
  acceptsPrivacyPolicy: z.literal(true, {
    message: 'You must accept the privacy policy',
  }),
  // Honeypot, P1. `website` is invisible to real students and legit clients
  // omit it, so `z.literal('')` lets both through. Spam bots fill every field
  // they see; a non-empty value here makes the server-side pipe answer 422 and
  // the client silently drop the submission. It is declared (not stripped as an
  // unknown key) precisely so the rule is enforced by the API, not just by the
  // form.
  website: z.literal('').optional(),
});

export const LoginSchema = z.object({
  email: iscaeEmail,
  password: z.string().min(8).max(PASSWORD_MAX),
});

export const VerifySchema = z.object({
  email: z.string().email(),
  code: z.string().length(6, 'Verification code must be 6 digits'),
});

export const ResendVerifySchema = z.object({
  email: z.string().email(),
});

export const RequestPasswordResetSchema = z.object({
  email: z.string().email(),
});

export const ResetPasswordSchema = z.object({
  email: z.string().email(),
  code: z.string().length(6),
  newPassword: strongPassword,
});

export const GrantRoleSchema = z.object({
  roleName: z.string().min(1),
});

export const AnonymiseUserSchema = z.object({});

/**
 * `PATCH /auth/me`. `avatarUrl` accepts only `null`, which removes the picture.
 *
 * On `origin/omar` it accepted any string and stored it, so a client could point
 * its avatar at any blob key or any URL. A picture is set by uploading one to
 * `POST /auth/me/avatar`, and the row stores the storage key, never a URL (RG93).
 */
export const UpdateProfileSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required').max(100).optional(),
  lastName: z.string().trim().min(1, 'Last name is required').max(100).optional(),
  avatarUrl: z.null().optional(),
});

/** `PATCH /auth/me/password`, from omar. The new password follows SignUp's rules. */
export const ChangePasswordSchema = z.object({
  oldPassword: z.string().min(1, 'Current password is required').max(PASSWORD_MAX),
  newPassword: strongPassword,
});

// ============================================================
// ANNOUNCEMENTS
// ============================================================

// Urls and file names are echoed into clients and mail bodies as-is (the
// escaped renderers only neuter them, they do not shorten them). Bounds here
// are the same defence every other free-text field gets.
export const AnnouncementPhotoSchema = z.object({
  url: z.string().max(2048),
  fileName: z.string().max(255),
});

const AnnouncementAttachmentSchema = z.object({
  url: z.string().max(2048),
  fileName: z.string().max(255),
});

export const CreateAnnouncementSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200),
  body: z.string().min(1, 'Body is required').max(20_000),
  category: z.nativeEnum(AnnouncementCategory),
  attachmentUrl: z.string().max(2048).optional().nullable(),
  attachmentName: z.string().max(255).optional().nullable(),
  attachments: z.array(AnnouncementAttachmentSchema).optional(),
  photos: z.array(AnnouncementPhotoSchema).optional(),
});

export const UpdateAnnouncementSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  body: z.string().min(1).max(20_000).optional(),
  category: z.nativeEnum(AnnouncementCategory).optional(),
  attachmentUrl: z.string().max(2048).optional().nullable(),
  attachmentName: z.string().max(255).optional().nullable(),
  attachments: z.array(AnnouncementAttachmentSchema).optional(),
  photos: z.array(AnnouncementPhotoSchema).optional(),
});

/**
 * `?page=&limit=`, the paging omar's announcement and notification screens send.
 * `limit` is his spelling of `pageSize`; the response is still section 2.6's
 * `{ items, page, pageSize, total }`. Capped at 100 like `PaginationSchema`.
 */
const PageLimitSchema = z.object({
  page: z.coerce.number().int().positive().catch(1),
  limit: z.coerce
    .number()
    .int()
    .positive()
    .catch(20)
    .transform((size) => Math.min(size, 100)),
});

/** `GET /announcements`. */
export const AnnouncementListQuerySchema = PageLimitSchema.extend({
  category: z.nativeEnum(AnnouncementCategory).optional(),
});

/** `GET /me/notifications`. */
export const NotificationListQuerySchema = PageLimitSchema;

/** RG26. */
export const NotificationPreferenceSchema = z.object({
  announcementsOptIn: z.boolean(),
});

// ============================================================
// COMPLAINTS
// ============================================================

export const CreateComplaintSchema = z.object({
  category: z.nativeEnum(ComplaintCategory),
  description: z.string().min(10, 'Description must be at least 10 characters').max(5000),
});

export const PostMessageSchema = z.object({
  body: z.string().min(1, 'Message body is required').max(5000),
});

/**
 * RG17, Omar's contract (0b3ae71): forward to a ROLE and let the API pick the
 * manager holding it. uml/18 section 5 names a `managerId` instead; the owner's
 * rule is kept (integration, Task 9) and the difference is in the phase C report.
 */
export const ForwardComplaintSchema = z.object({
  targetRole: z.string().min(1),
});

/** RG139. */
export const CloseWithoutConfirmationSchema = z.object({
  reason: z.string().min(1).max(1000).optional(),
});

/**
 * `GET /complaints`, Omar's query: `scope` mine or assigned (anything else is
 * "all", for PRESIDENT and ADMIN), `status` one or a comma list of his statuses,
 * `category`, and `page`/`limit`.
 */
export const ComplaintQuerySchema = PageLimitSchema.extend({
  scope: z.string().max(50).optional(),
  status: z.string().max(100).optional(),
  category: z.nativeEnum(ComplaintCategory).optional(),
});

/** `GET /complaints/history`. RG20, RG22. */
export const ComplaintHistoryQuerySchema = PageLimitSchema.extend({
  category: z.nativeEnum(ComplaintCategory).optional(),
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
});

// ============================================================
// ADMIN - USER MANAGEMENT
// ============================================================

/** `GET /users`. Paged by `page` and `pageSize` like every other list, section 2.6. */
export const UserListQuerySchema = PaginationSchema.extend({
  search: z.string().trim().max(100).optional(),
  role: z.string().max(50).optional(),
});

/** `PATCH /users/:id`. Names only - an avatar is its owner's to upload. */
export const UpdateUserAdminSchema = z.object({
  firstName: z.string().trim().min(1).max(100).optional(),
  lastName: z.string().trim().min(1).max(100).optional(),
});

// ============================================================
// EVENTS
// ============================================================

export * from './event.schemas';

// ============================================================
// CARPOOLING
// ============================================================

export * from './carpooling.schemas';

// ============================================================
// LIBRARY - uml/18 section 9
// ============================================================

export * from './library';

// ============================================================
// PUSH NOTIFICATIONS
// ============================================================

export const PushSubscriptionKeysSchema = z.object({
  p256dh: z.string().min(10),
  auth: z.string().min(10),
});

export const SavePushSubscriptionSchema = z.object({
  endpoint: z.string().url(),
  keys: PushSubscriptionKeysSchema,
});

export const DeletePushSubscriptionSchema = z.object({
  endpoint: z.string().url(),
});

export type SavePushSubscriptionDto = z.infer<typeof SavePushSubscriptionSchema>;
export type DeletePushSubscriptionDto = z.infer<typeof DeletePushSubscriptionSchema>;
